import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useAddOrganizationCreditsMutation } from "modules/backoffice/hooks/organizations/useAddOrganizationCreditsMutation"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Route as CreditsRoute } from "routes/_base/_auth/backoffice/organizations/$id/credits/list"
import { z } from "zod"

const AddCreditsFormSchema = z.object({
  credits: z.int().positive(),
})

type Props = {
  onOpenChange: () => void
}

export const BackofficeAddCreditsModal = ({ onOpenChange }: Props) => {
  const { t } = useTranslation()
  const { id } = CreditsRoute.useParams()

  const form = useForm<z.infer<typeof AddCreditsFormSchema>>({
    resolver: zodResolver(AddCreditsFormSchema),
    defaultValues: { credits: 0 },
    mode: "onChange",
  })

  const { mutateAsync, isPending } = useAddOrganizationCreditsMutation({
    id,
    credits: form.watch("credits"),
    onSuccess: () => onOpenChange(),
  })

  const onSubmit = async () => {
    await mutateAsync(id)
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("backoffice.credits.add")}</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <FormField
              control={form.control}
              name="credits"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("backoffice.credits.table.credits")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      step={100}
                      {...form.register(field.name, { valueAsNumber: true, min: 100, max: 2000 })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button disabled={isPending || !form.formState.isValid}>{t("shared.save")}</Button>
            </DialogFooter>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
